import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { toast } from "sonner";

const STAGE_ACCENTS = {
  "New Lead": "#3B82F6",
  "Documents Collected": "#F59E0B",
  "University Applied": "#8B5CF6",
  "Accepted": "#10B981",
  "Visa Processing": "#F97316",
  "Enrolled": "#14B8A6",
};

export default function Kanban() {
  const { t, ts, tc } = useI18n();
  const [stages, setStages] = useState([]);
  const [students, setStudents] = useState([]);
  const [draggingOver, setDraggingOver] = useState(null);

  const load = async () => {
    const [m, s] = await Promise.all([api.get("/meta"), api.get("/students")]);
    setStages(m.data.stages);
    setStudents(s.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const onDragStart = (e, id) => {
    e.dataTransfer.setData("text/plain", id);
    e.currentTarget.classList.add("dragging");
  };
  const onDragEnd = (e) => e.currentTarget.classList.remove("dragging");
  const onDragOver = (e) => e.preventDefault();

  const onDrop = async (e, stage) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    setDraggingOver(null);
    const prev = students.find((s) => s.id === id);
    if (!prev || prev.stage === stage) return;
    setStudents((arr) => arr.map((s) => (s.id === id ? { ...s, stage } : s)));
    try {
      await api.patch(`/students/${id}`, { stage });
      toast.success(`${prev.name} → ${stage}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setStudents((arr) => arr.map((s) => (s.id === id ? { ...s, stage: prev.stage } : s)));
    }
  };

  return (
    <Layout>
      <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1500px] mx-auto">
        <div className="mb-6">
          <div className="eyebrow">{t("appName")}</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#0B192C] mt-1">
            {t("pipeline")}
          </h1>
        </div>

        <div className="flex gap-4 overflow-x-auto kanban-scroll pb-4" data-testid="kanban-board">
          {stages.map((st) => {
            const col = students.filter((s) => s.stage === st);
            return (
              <div key={st}
                   className={`kanban-col ${draggingOver === st ? "drop-active" : ""}`}
                   onDragOver={(e) => { onDragOver(e); setDraggingOver(st); }}
                   onDragLeave={() => setDraggingOver(null)}
                   onDrop={(e) => onDrop(e, st)}
                   data-testid={`col-${st}`}>
                <div className="px-3 py-3 border-b border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: STAGE_ACCENTS[st] }} />
                    <div className="font-semibold text-sm text-[#0B192C]">{ts(st)}</div>
                  </div>
                  <span className="text-xs text-slate-500 bg-white border border-slate-200 rounded-md px-1.5 py-0.5">
                    {col.length}
                  </span>
                </div>
                <div className="p-2 space-y-2 min-h-[100px]">
                  {col.map((s) => (
                    <Card key={s.id}
                          draggable
                          onDragStart={(e) => onDragStart(e, s.id)}
                          onDragEnd={onDragEnd}
                          className="kanban-card"
                          data-testid={`kanban-card-${s.id}`}>
                      <Link to={`/students/${s.id}`} className="block">
                        <div className="font-medium text-sm text-[#0B192C]">{s.name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{s.nationality || "—"}</div>
                        {s.target_countries?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {s.target_countries.slice(0, 3).map((c) => (
                              <span key={c} className="px-1.5 py-0.5 text-[10px] rounded-md bg-slate-100 text-slate-600">{tc(c)}</span>
                            ))}
                          </div>
                        )}
                      </Link>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
